import { createContext, useContext, useEffect, useState } from "react";
import { useDispatch } from "react-redux";

import {
	useLoginAsUserMutation,
	useLoginMutation,
	useLogoutMutation,
} from "./authApiSlice";

import apiSlice from "../../app/api/apiSlice";
import { useGetCurrentUserQuery } from "../user/userApiSlice";

const AuthContext = createContext();

const SHOULD_NOT_FETCH_USER_ON_PATHS = ["/offers/emails"];

export const AuthProvider = ({ children }) => {
	const currentPath = window.location.pathname;
	const dispatch = useDispatch();
	const [login, { isLoading: isSigningIn }] = useLoginMutation();
	const [loginAsUser, { isLoading: isSigningInAsUser }] =
		useLoginAsUserMutation();
	const [loginAsUserId, setLoginAsUserId] = useState(null);
	const [logout, { isLoading: isSigningOut }] = useLogoutMutation();
	const [user, setUser] = useState(null);
	const [loggedIn, setLoggedIn] = useState(null);
	const { refetch } = useGetCurrentUserQuery(undefined, {
		skip:
			loggedIn !== null || SHOULD_NOT_FETCH_USER_ON_PATHS.includes(currentPath),
	});

	useEffect(() => {
		const fetchUserData = async () => {
			try {
				const refetchPromise = await refetch();
				if (refetchPromise.status === "fulfilled") {
					setUser(refetchPromise?.data);
					setLoggedIn(true);
				} else {
					setLoggedIn(false);
				}
			} catch (_error) {
				setLoggedIn(false);
			}
		};

		if (SHOULD_NOT_FETCH_USER_ON_PATHS.includes(currentPath)) {
			setLoggedIn(false);
			return;
		}

		if (loggedIn === null) {
			fetchUserData();
		}
	}, [loggedIn, refetch, currentPath]);

	const signIn = async (email, password) => {
		return login({ email, password })
			.unwrap()
			.then((payload) => {
				setUser(payload.user);
				setLoggedIn(true);
				return payload.user;
			});
	};

	const signInAsUser = async (userId) => {
		setLoginAsUserId(userId);

		const user = await loginAsUser({ userId })
			.unwrap()
			.then((payload) => {
				setUser(payload.user);
				setLoggedIn(true);
				setLoginAsUserId(null);
				return payload.user;
			});

		return user;
	};

	const signOut = async () => {
		try {
			await logout().unwrap();

			dispatch(apiSlice.util.resetApiState());
			setUser(null);
			setLoggedIn(false);
			setTimeout(() => {
				dispatch(apiSlice.endpoints.getCurrentBranding.initiate());
			}, 300);
		} catch (error) {
			console.log("Error during sign out:", error);
		}
	};

	const value = {
		user,
		loggedIn,
		signIn,
		isSigningIn,
		signOut,
		isSigningOut,
		signInAsUser,
		isSigningInAsUser,
		loginAsUserId,
	};

	return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};

export const useAuth = () => useContext(AuthContext);
